<template>
    <div class="relative h-screen overflow-y-auto bg-gray-200">
        <!--
          `sticky` makes this a stacking context, so dropdowns in the header slots cannot rise above the page
          body on their own: `z-40` lifts the whole header over it instead.
        -->
        <header class="sticky left-0 z-40">
            <div
                v-if="slots.breadcrumb || slots.search"
                class="bg-gray-100 border-b border-b-gray-200 text-sm flex flex-row items-center justify-between px-4 min-h-12"
            >
                <slot name="breadcrumb" />

                <slot name="search" />
            </div>

            <div class="bg-gray-50 relative py-4 pr-4" :class="[props.variant === 'Logo' ? 'pl-36' : 'pl-4']">
                <div
                    v-if="props.variant === 'Logo'"
                    class="bg-gray-50 ring-1 ring-gray-200 size-28 rounded-lg absolute left-4 top-2 p-2 flex items-center justify-center"
                    :class="{ 'border-4 border-red-700 bg-red-200': logoError }"
                >
                    <p v-if="logoError" class="flex flex-col items-center gap-1 p-1 text-center leading-tight">
                        {{ logoError.label }}
                        <HelpButton status="Error" :text="logoError.explanation" />
                    </p>

                    <Tooltip>
                        <template #trigger="tooltipProps">
                            <img
                                v-if="props.logoUrl && !logoError"
                                :src="props.logoUrl"
                                :alt="props.logoAlt"
                                v-bind="tooltipProps"
                                @error="onImageError"
                            />

                            <p
                                v-if="!props.logoUrl && props.logoState === 'Default'"
                                class="text-ellipsis hyphens-auto overflow-hidden max-h-28 max-w-28 px-2 leading-tight"
                            >
                                {{ props.logoAlt }}
                            </p>
                        </template>

                        <template #text>{{ $t('logo-edit-hint') }}</template>
                    </Tooltip>

                    <div
                        v-if="props.logoState === 'Processing'"
                        class="absolute inset-0 flex items-center justify-center rounded-lg bg-white/60"
                    >
                        <PendingIcon class="size-16 animate-spin text-gray-500" />
                    </div>

                    <!-- Rendered before its text changes, as screen readers only announce changes of existing live regions -->
                    <p role="status" class="sr-only">{{ logoStatusText }}</p>
                </div>
                <div class="flex flex-wrap gap-4 items-center justify-between">
                    <div class="flex gap-4 items-center font-semibold text-5xl">
                        <div v-if="$slots.icon" class="text-gray-600">
                            <slot name="icon" />
                        </div>
                        <h1 v-if="$slots.headline" class="hyphens-auto text-gray-700">
                            <slot name="headline"></slot>
                        </h1>
                        <slot name="headline-extra" />
                    </div>

                    <div class="flex flex-wrap gap-2">
                        <slot name="buttons" />
                    </div>
                </div>
            </div>

            <div class="bg-gray-100 border-t border-t-gray-200" :class="[props.variant === 'Logo' ? 'pl-36' : 'pl-4']">
                <slot name="tab-navigation" />
            </div>
        </header>

        <main>
            <slot name="body" />
        </main>
    </div>
</template>

<script setup lang="ts">
import { useImageError } from '@/utils/useImageError';
import { useFluent } from 'fluent-vue';
import { computed, useSlots, watch } from 'vue';
import HelpButton from '../HelpButton/HelpButton.vue';
import PendingIcon from '../Icon/PendingIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';

type HeaderCardsLayoutProps = {
    logoUrl?: string;
    logoAlt?: string;
    logoState?: 'Default' | 'Processing' | 'Processing-Failed';
    variant: 'Logo' | 'No-Logo';
};

const props = withDefaults(defineProps<HeaderCardsLayoutProps>(), { logoState: 'Default' });
const fluent = useFluent();

const logoStatusText = computed(() => {
    switch (props.logoState) {
        case 'Processing':
            return fluent.$t('logo-processing');
        case 'Processing-Failed':
            return fluent.$t('logo-processing-failed');
        case 'Default':
            return '';
    }
});

const slots = useSlots();

defineEmits<{
    (e: 'enterEditMode'): void;
    (e: 'leaveEditMode'): void;
    (e: 'discardChanges'): void;
    (e: 'save'): void;
}>();

const { hasLoadingImageFailed, onImageError, setHasLoadingImageFailed } = useImageError(false);

// A new logo can arrive without a reload, an earlier load error must not hide it
watch(
    () => props.logoUrl,
    () => setHasLoadingImageFailed(false),
);

const logoError = computed(() => {
    if (props.logoState === 'Processing-Failed') {
        return {
            label: fluent.$t('logo-processing-failed-short'),
            explanation: fluent.$t('logo-processing-failed'),
        };
    }
    if (hasLoadingImageFailed.value) {
        return {
            label: fluent.$t('loading-logo-failed-short'),
            explanation: fluent.$t('loading-logo-failed'),
        };
    }
    return undefined;
});
</script>
